import type { IFolderState } from './types';
import type { ISearchResult } from '../content-browser/types';

import { unionBy } from 'lodash';

import * as ActionTypes from 'src/state/action-types';

export const initialState: IFolderState = {
  folder: [],
};

export default function folderReducer(
  state: IFolderState = initialState,
  action: ActionTypes.ActionTypes
): IFolderState {
  switch (action.type) {
    case ActionTypes.CONTENT_BROWSER_SEARCH_SUCCESS: {
      const folders = action.payload.results
        .filter((result: ISearchResult) => result.index === 'folder')
        .map((result: ISearchResult) => ({
          id: result.id,
          name: result.source.name,
          parent_id: result.source.folder_id || 'root',
          workspace_id: result.source.workspace_id || 'private',
          color: result.source.color,
        }));

      return {
        ...state,
        folder: unionBy(state.folder.concat(folders), 'id'),
      };
    }

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_REQUEST: {
      // Optimistic update: immediately update parent_id and workspace_id for moved folders
      const { folderIds, targetFolderId, targetWorkspaceId } = action.payload;

      return {
        ...state,
        folder: state.folder.map((item) =>
          folderIds.includes(item.id)
            ? {
                ...item,
                parent_id: targetFolderId,
                workspace_id: targetWorkspaceId,
              }
            : item
        ),
      };
    }

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_FAILURE: {
      // Rollback optimistic update on failure
      const { originalData } = action.payload;

      return {
        ...state,
        folder: state.folder.map((item) => {
          const originalItem = originalData.folderItems.find((orig) => orig.id === item.id);
          return originalItem
            ? {
                ...item,
                parent_id: originalItem.parent_id,
                workspace_id: originalItem.workspace_id,
              }
            : item;
        }),
      };
    }

    case ActionTypes.FOLDER_CREATE_REQUEST: {
      // Optimistic update: add folder with temporary ID
      const { tempId, name, parent_id, workspace_id, color } = action.payload;

      return {
        ...state,
        folder: [...state.folder, { id: tempId, name, parent_id, workspace_id, color }],
      };
    }

    case ActionTypes.FOLDER_CREATE_SUCCESS: {
      // Replace temporary ID with real ID from server
      const { tempId, id, name, parent_id, workspace_id, color } = action.payload;

      return {
        ...state,
        folder: state.folder.map((folder) =>
          folder.id === tempId ? { id, name, parent_id, workspace_id, color } : folder
        ),
      };
    }

    case ActionTypes.FOLDER_CREATE_FAILURE: {
      // Rollback: remove the optimistically added folder
      const { tempId } = action.payload;

      return {
        ...state,
        folder: state.folder.filter((folder) => folder.id !== tempId),
      };
    }

    case ActionTypes.FOLDER_UPDATE_SUCCESS: {
      // Update folder name and color in state
      const { id, name, color } = action.payload;

      return {
        ...state,
        folder: state.folder.map((folder) =>
          folder.id === id ? { ...folder, name, ...(color !== undefined && { color }) } : folder
        ),
      };
    }

    default:
      return state;
  }
}
